import { useEffect, useState, useRef } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';

import { screenSize } from 'lib/utils';
import HomepageCarouselItem from './homepage_carousel_item';
import HomepageCarouselPauseButton from './homepage_carousel_pause';
import HomepageCarouselButtons from './homepage_carousel_buttons';
import HomepagePaginationDots from './homepage_carousel_pagination';

const HomepageCarousel = ({ dataProps, mobileProps }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [promoData, setPromoData] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isAutoplayPaused, setAutoplayPaused] = useState(false);
  const activeSlideRef = useRef(0);
  const carouselRef = useRef(null);
  const carouselVisible = useRef(false); // is the carousel element currently in the viewport
  const viewedCarouselItems = useRef([]); // the carousel slides which have already been seen
  const observerTarget = useRef(null); // the carousel element to be observed

  const checkPromoVisibility = () => {
    if (
      carouselVisible.current &&
      !viewedCarouselItems.current.includes(activeSlideRef.current)
    ) {
      const propsToUse = isMobile
        ? JSON.parse(mobileProps)
        : JSON.parse(dataProps);

      // add to list of viewed carousels to prevent duplicate values in the datalayer
      viewedCarouselItems.current.push(activeSlideRef.current);

      window.dataLayer.push({
        event: 'view_promotion_ga4',
        ecommerce: {
          promotion_name: propsToUse[activeSlideRef.current].title,
          promotion_id: propsToUse[activeSlideRef.current].id,
          creative_slot: `slot ${activeSlideRef.current}`,
          location: 'banner',
        },
      });
    }
  };

  const toggleAutoplay = () => {
    setAutoplayPaused(prevPaused => !prevPaused);
  };

  const handleSlideChange = splide => {
    setActiveSlide(splide.index);
    activeSlideRef.current = splide.index;
    checkPromoVisibility();
  };

  const handleCarouselLinkClick = index => {
    setActiveSlide(index);
    activeSlideRef.current = index;
    checkPromoVisibility();
    if (carouselRef.current) {
      carouselRef.current.go(index);
    }
  };

  const handleCarouselItemClick = () => {
    const propsToUse = isMobile
      ? JSON.parse(mobileProps)
      : JSON.parse(dataProps);

    window.dataLayer.push({
      event: 'select_promotion_ga4',
      ecommerce: {
        promotion_name: propsToUse[activeSlideRef.current].title,
        promotion_id: propsToUse[activeSlideRef.current].id,
        creative_slot: `slot ${activeSlideRef.current}`,
        location: 'banner',
      },
    });
  };

  const renderCarouselItems = () =>
    promoData.map((promo, index) => (
      <SplideSlide key={`slide-${promo.id}`}>
        <HomepageCarouselItem
          promo={promo}
          onClick={() => handleCarouselItemClick()}
          isActive={index === activeSlide}
        />
      </SplideSlide>
    ));

  const observerCallback = entries => {
    entries.forEach(entry => {
      carouselVisible.current = entry.isIntersecting;
      checkPromoVisibility();
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const observerOptions = {
      // threshold at which the carousel is considered visible
      threshold: 0.25,
    };

    const observer = new IntersectionObserver(
      entries => observerCallback(entries),
      observerOptions,
    );

    const handleResize = () => setIsMobile(screenSize() < 768);

    window.addEventListener('resize', handleResize);
    handleResize();

    if (!observerTarget.current) {
      observerTarget.current = document.querySelector(
        '.homepage-carousel-container .splide',
      );
      if (observerTarget.current) {
        observer.observe(observerTarget.current);
      }
    }

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', handleResize);
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, []);

  useEffect(() => {
    const propsToUse = isMobile
      ? JSON.parse(mobileProps)
      : JSON.parse(dataProps);
    setPromoData(propsToUse);
  }, [isMobile]);

  useEffect(() => {
    const autoplayInterval = 5000;
    let intervalId = null;

    const handleAutoplay = () => {
      if (!isAutoplayPaused && carouselRef.current) {
        const nextIndex = (activeSlide + 1) % promoData.length;
        carouselRef.current.go(nextIndex);
        setActiveSlide(nextIndex);
        activeSlideRef.current = nextIndex;
        checkPromoVisibility();
      }
    };

    const startAutoplay = () => {
      intervalId = setInterval(handleAutoplay, autoplayInterval);
    };

    const stopAutoplay = () => {
      clearInterval(intervalId);
    };

    if (!isAutoplayPaused) {
      startAutoplay();
    } else {
      stopAutoplay();
    }

    // This cleanup function clears the autoplay interval
    // when the component unmounts or when any of the dependencies change.
    return () => {
      stopAutoplay();
    };
  }, [isAutoplayPaused, activeSlide, promoData.length]);

  return (
    <div
      className="homepage-carousel-container"
      data-testid="homepage-carousel-container">
      {!isMobile && (
        <div className="autoplay-toggle" data-testid="play-pause-button">
          <HomepageCarouselPauseButton
            isPaused={isAutoplayPaused}
            onClick={toggleAutoplay}
          />
        </div>
      )}

      <Splide
        options={{
          type: 'slide',
          rewind: false,
          drag: isMobile,
          autoplay: !isAutoplayPaused,
          interval: 5000,
          pauseOnHover: false,
          lazyLoad: 'nearby',
          pagination: false,
          arrows: false,
        }}
        onMoved={handleSlideChange}
        ref={carouselRef}>
        {renderCarouselItems()}
      </Splide>

      <div className="mt-8 fl ai-c jc-c m-mt-0">
        {isMobile && (
          <>
            <HomepagePaginationDots
              totalSlides={promoData.length}
              activeIndex={activeSlide}
              onClick={handleCarouselLinkClick}
            />
            <HomepageCarouselPauseButton
              isPaused={isAutoplayPaused}
              onClick={toggleAutoplay}
            />
          </>
        )}
      </div>

      {!isMobile && (
        <HomepageCarouselButtons
          promoData={promoData}
          activeSlide={activeSlide}
          onClick={handleCarouselLinkClick}
        />
      )}
    </div>
  );
};

export default HomepageCarousel;
